import { z } from "zod";
import { Printable } from "./feedback.ts";
import { SCHEDULE_WINDOW_MAX_DAYS, parseLocalDate } from "./schedule.ts";

/**
 * Чат занятия: срок хранения, потолок тела, ключ занятия и правило окна письма.
 *
 * Проект целиком в `docs/room-chat-design.md`. Величины срока и потолка обязаны
 * быть ОДНИМИ И ТЕМИ ЖЕ у базы, у уборщика и у экрана; ключ и окно письма живут
 * здесь по той же причине: их считает сервер, а называет словами экран.
 *
 * ⚠️ **Срок живёт константой пакета, а не переменной окружения**, ровно как
 * `ANNOUNCEMENT_RETENTION_DAYS` рядом. Число это придётся назвать человеку
 * («сообщения хранятся 30 дней») и назвать в строке политики обработки, а срок,
 * объявленный одним местом и соблюдаемый другим, разъезжается: так уже
 * разъехалась телеметрия ICE, где срок был решён, экран его показывал, а данные
 * лежали вечно.
 */

/**
 * Через сколько суток после ФАКТИЧЕСКОГО конца занятия стирается содержимое
 * сообщений (раздел 6 проекта).
 *
 * Тем же числом, что у рассылки, и это не совпадение: там и тут свободный текст
 * про занятие и про детей, живущий у нас без иной причины, кроме удобства
 * чтения задним числом. Два срока для одного класса текста означали бы две
 * строки в политике обработки и два места, которые разойдутся.
 *
 * ⚠️ Рубеж считается от даты ЗАНЯТИЯ, а не от `created_at` строки: у
 * перенесённого занятия в ключе стоит исходная дата, а идёт оно в новую.
 * Считает его `eraseExpiredOccurrenceChatBodies` в
 * `apps/api/src/repo/occurrence-chat.ts`, и это единственное место.
 */
export const OCCURRENCE_CHAT_RETENTION_DAYS = 30;

/**
 * Потолок длины одного сообщения.
 *
 * ⚠️ **Число взято у соседа (`ANNOUNCEMENT_BODY_MAX`), а не придумано**, и это
 * решение, а не копипаста. Класс текста тот же самый: свободный ввод человека в
 * колонку `text`, куда попадает и набранное руками, и вставленное из PDF.
 * Третье число здесь означало бы третье правило про одно и то же, а такие
 * правила расходятся молча: разойдясь, они дают отказ на одном экране и тишину
 * на соседнем при одинаковом тексте.
 *
 * ⚠️ Потолок ответа в нитке объявления вдвое меньше, и брать надо было НЕ его.
 * Он посчитан от веса СТРАНИЦЫ (отдача в режиме белого списка обрывается около
 * 16 КБ, раздел 3 CLAUDE.md), то есть отвечает на вопрос «сколько влезет в один
 * ответ», а не «сколько человеку дать написать». У чата занятия этот вопрос
 * закроет потолок страницы вместе с ручкой чтения, и решать его двумя разными
 * рычагами сразу значит недодать одному человеку ради арифметики другого.
 */
export const OCCURRENCE_CHAT_BODY_MAX = 2000;

/*
  ⚠️ **Это же число стоит вторым экземпляром в `CHECK
  occurrence_chat_message_body_len`**, и списать его оттуда в общий пакет
  нельзя: миграция это застывшая история, а импорт живой константы переписывал
  бы её задним числом. Следствие для того, кто станет двигать потолок: правка
  ОДНОГО этого числа даёт не «стало можно длиннее», а пятисотку из базы на
  первом же длинном сообщении. Потолок двигается миграцией, и только вместе с
  ней. Ровно так же живут `ANNOUNCEMENT_BODY_MAX` и его `CHECK`.
*/

/**
 * ⚠️ **`Printable` обязателен, а не аккуратен**, и довод тот же, что у тела
 * объявления: PostgreSQL не хранит нулевой байт ни в `text`, ни в `jsonb`,
 * поэтому управляющий символ из копипасты падает ошибкой драйвера, то есть
 * пятисоткой. Писать здесь второе регулярное выражение нельзя: копия однажды
 * отстанет от оригинала, и пятисотка вернётся ровно там, где отстала.
 */
export const OccurrenceChatBody = Printable.trim().min(1).max(OCCURRENCE_CHAT_BODY_MAX);
export type OccurrenceChatBody = z.infer<typeof OccurrenceChatBody>;

/* ─────────────────────────── ключ и адрес ─────────────────────────────── */

/**
 * Ключ чата: чему принадлежит эта переписка.
 *
 * ⚠️ **Свой тип, а НЕ `OccurrenceKey` из `./student-profile.ts`, и расширять тот
 * нельзя.** Форм здесь три (`pair` у серии одной пары, `group` у занятия
 * группы, `lesson` у разового и ручного), а у отметки посещения их две, и
 * групповой формы там не может быть никогда: отметка держится на ключе занятия
 * ОДНОЙ пары (решение владельца 21.08.2026, раздел 5.10 CLAUDE.md). Добавив
 * третью форму в общий тип, мы заставили бы каждого читателя отметок разбирать
 * ветку, которой он не увидит ни разу, а первый же `switch` без неё стал бы
 * молча неполным.
 *
 * ⚠️ **Тот же тип служит АДРЕСОМ, но адрес и ключ это не всегда одно значение.**
 * Мест адресации две (раздел 3 проекта): из комнаты клиент адресует УРОКОМ, из
 * расписания парой «слот и дата». Сервер сводит оба к ключу, и разыменование
 * живёт в ОДНОЙ функции (`resolveOccurrenceChat` в
 * `apps/api/src/repo/occurrence-chat-access.ts`): адрес `pair` у серии группы
 * разрешается в ключ `group`, адрес `lesson` у урока занятия разрешается в
 * `pair` либо в `group`.
 *
 * ⚠️ **Ключ ФИКСИРУЕТСЯ в строке при отправке, а не выводится заново при
 * чтении.** У урока `slot_id` это слабая ссылка (`ON DELETE SET NULL`), и при
 * пересоздании слота она обнуляется, то есть выводимый ключ менял бы форму на
 * середине жизни урока. Записанный не меняет ничего.
 */
export type OccurrenceChatKey =
  | { kind: "pair"; slotId: string; occursOn: string }
  | { kind: "group"; groupId: string; occursOn: string }
  | { kind: "lesson"; lessonId: string };

/**
 * Ключ одной строкой: `pair:<слот>:<дата>`, `group:<группа>:<дата>`,
 * `lesson:<урок>`.
 *
 * ⚠️ **Сборка и разбор живут ЗДЕСЬ, а не в маршруте и не на экране**, тем же
 * приёмом, что у `conversationId`: строка ездит в адресе четырёх ручек и в
 * строках счётчика, а то, что четыре места собирают конкатенацией, разъедется
 * на первой же правке разделителя.
 */
export const OccurrenceChatId = z
  .string()
  .regex(/^(pair|group):[0-9a-f-]{36}:\d{4}-\d{2}-\d{2}$|^lesson:[0-9a-f-]{36}$/i);
export type OccurrenceChatId = z.infer<typeof OccurrenceChatId>;

export function occurrenceChatId(key: OccurrenceChatKey): string {
  if (key.kind === "lesson") return `lesson:${key.lessonId}`;
  const id = key.kind === "pair" ? key.slotId : key.groupId;
  return `${key.kind}:${id}:${key.occursOn}`;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Разбор адреса. `null` означает «мусор», и вызывающий обязан ответить 404, а не
 * 400: снаружи кривой адрес неотличим от несуществующего занятия, и различать их
 * значило бы подсказывать перебирающему (раздел 9 CLAUDE.md).
 */
export function parseOccurrenceChatId(raw: string): OccurrenceChatKey | null {
  const parts = raw.split(":");
  const [kind, id, on] = parts;
  if (id === undefined || !UUID.test(id)) return null;
  if (kind === "lesson") return parts.length === 2 ? { kind, lessonId: id } : null;
  if (parts.length !== 3 || on === undefined) return null;
  /**
   * ⚠️ Разбор датой, а не одной лишь формой: 31 февраля форму проходит, а
   * занятия в такой день не бывает, и дальше эта строка уехала бы в раскрытие
   * расписания. Ровно так же поступает `occurrenceTarget` у ручек занятия.
   */
  if (parseLocalDate(on) === null) return null;
  if (kind === "pair") return { kind, slotId: id, occursOn: on };
  if (kind === "group") return { kind, groupId: id, occursOn: on };
  return null;
}

/* ────────────────────────── окно письма ───────────────────────────────── */

/**
 * Сколько ЧАСОВ после конца занятия в чат ещё можно писать (раздел 4 проекта).
 *
 * ⚠️ **Сутки, а не неделя.** Семь суток смыкали бы чаты соседних занятий
 * недельной серии в непрерывный канал без единого часа тишины, а граница это
 * единственное, чем «чат в контексте урока» отличается от постоянного канала
 * связи; на этом отличии стоит разрешающая формулировка раздела 7 CLAUDE.md.
 */
export const OCCURRENCE_CHAT_WRITE_AFTER_HOURS = 24;

/**
 * Насколько далеко ВПЕРЁД занятие ещё принимает сообщения.
 *
 * ⚠️ **Горизонт обязателен, и это не перестраховка:** серия бесконечна, у
 * занятия нет строки в базе, пока оно не стало уроком, и без горизонта ребёнок,
 * подставив дату, пишет в занятие через год. Число взято у сетки расписания, а
 * не назначено своё: горизонт, который шире экрана, означал бы чат, который
 * нигде не показывается, а уже, чем экран, означал бы кнопку, отвечающую
 * отказом.
 */
export const OCCURRENCE_CHAT_AHEAD_DAYS = SCHEDULE_WINDOW_MAX_DAYS;

/**
 * Можно ли писать в чат этого занятия прямо сейчас (раздел 4 проекта).
 *
 * Чистая функция: часы и все четыре опоры приезжают аргументами, потому что
 * правило это ЕДИНСТВЕННОЕ место, где считается окно письма, и проверяться оно
 * обязано без базы.
 *
 * ⚠️ **Конец считается от ФАКТИЧЕСКОГО момента, а не от даты в ключе.**
 * Перенесённое занятие хранит в ключе исходную дату, а идёт в новое время, и
 * счёт от ключа закрыл бы чат до самого занятия. Момент сюда приходит уже
 * разрешённым (`resolveOccurrence`), то есть с учётом переноса и правки
 * длительности.
 *
 * ⚠️ **Окно ПИСЬМА завершение урока не закрывает** (решение владельца, раздел 4
 * проекта): «после» это половина фичи. Чтение ученику с закрытым архивом
 * завершение закрывает, но это рубеж архива (`occurrenceChatTimeGate`), а не это
 * правило. У занятия серии `endedAt` сюда приезжает, но не читается; у ручного
 * он служит НАЧАЛОМ отсчёта суток, а не концом окна.
 *
 * ⚠️ **У ручного урока планового конца нет вовсе.** Там сутки после завершения,
 * а если урок так и не завершён, сутки после последнего сообщения; пустой чат
 * такого урока открыт всегда, иначе его нельзя было бы начать.
 */
export function occurrenceChatWriteOpen(args: {
  now: number;
  /** Момент начала занятия. `null` у ручного урока: времени у него нет вовсе. */
  startsAt: number | null;
  /** Момент планового КОНЦА. `null` там же, где `startsAt`. */
  endsAt: number | null;
  /** Когда урок завершили. Спрашивается только у формы без планового конца. */
  endedAt: number | null;
  /** Последнее сообщение чата. Последняя опора у незавершённого ручного урока. */
  lastMessageAt: number | null;
  /** Занятие отменено: писать нельзя, читать можно. */
  cancelled: boolean;
}): boolean {
  if (args.cancelled) return false;
  const after = OCCURRENCE_CHAT_WRITE_AFTER_HOURS * 3_600_000;

  if (args.startsAt !== null) {
    if (args.startsAt - args.now > OCCURRENCE_CHAT_AHEAD_DAYS * 86_400_000) return false;
    return args.now <= (args.endsAt ?? args.startsAt) + after;
  }

  /**
   * Ручной урок. Горизонта вперёд у него нет и не нужно: строка урока уже
   * существует, то есть адрес не перебирается датами, и подставить занятие
   * через год нечем.
   */
  const from = args.endedAt ?? args.lastMessageAt;
  return from === null || args.now <= from + after;
}

/* ──────────────────────────── сообщения ───────────────────────────────── */

/**
 * Одно сообщение чата занятия.
 *
 * ⚠️ Состояний три, а полей под них два, и разбирает эту пару та же
 * `announcementReplyState`, что и в переписке кабинета: «удалено человеком» и
 * «стёрто по сроку хранения» это разные события с разными текстами (раздел 13
 * проекта), а второе правило разбора одной и той же пары разошлось бы с первым.
 */
export const OccurrenceChatMessage = z.object({
  id: z.string().uuid(),
  /**
   * Как автора зовут ДЛЯ ЭТОГО ЧИТАТЕЛЯ. Считает `authorNameFor`, единственное
   * место сборки имени автора сообщения; правило подписей комнаты тут ни при
   * чём, разбор в `apps/api/src/repo/occurrence-chat-messages.ts`.
   */
  authorName: z.string(),
  mine: z.boolean(),
  /** `null` это «удалено» либо «стёрто по сроку»: различает их `deletedAt`. */
  body: z.string().nullable(),
  createdAt: z.number().int(),
  deletedAt: z.number().int().nullable(),
});
export type OccurrenceChatMessage = z.infer<typeof OccurrenceChatMessage>;

/**
 * Сколько сообщений едет в одной странице.
 *
 * Дюжина, тем же числом и по тем же замерам, что у `CONVERSATION_PAGE_LIMIT`:
 * потолок тела там и тут один (2000 знаков), значит худшая страница весит
 * столько же, 13,9 КБ сжатых при отсечке около 16 КБ (раздел 3 CLAUDE.md).
 * Потолок здесь означает размер порции, а не границу видимого: у страницы есть
 * курсор.
 */
export const OCCURRENCE_CHAT_PAGE_LIMIT = 12;

export const SendOccurrenceChatMessageRequest = z.object({ body: OccurrenceChatBody });
export type SendOccurrenceChatMessageRequest = z.infer<typeof SendOccurrenceChatMessageRequest>;

export const OccurrenceChatPageResponse = z.object({
  /** Адрес, которым спрашивали. Клиент возвращает его как есть. */
  id: OccurrenceChatId,
  /**
   * Пара или группа. От этого зависят тексты панели («Читают все в группе») и
   * пустое состояние.
   *
   * ⚠️ Ученику это НЕ утечка вида занятия сверх уже принятой: групповой чат
   * существует только при ОТКРЫТОМ составе (раздел 5 проекта), а состав,
   * открытый преподавателем, ученик и так видит в окне переписки.
   */
  scope: z.enum(["pair", "group"]),
  /** Открыто ли окно письма прямо сейчас. Считает `occurrenceChatWriteOpen`. */
  canWrite: z.boolean(),
  items: z.array(OccurrenceChatMessage),
  nextCursor: z.string().nullable(),
});
export type OccurrenceChatPageResponse = z.infer<typeof OccurrenceChatPageResponse>;

/* ─────────────────────── счётчик непрочитанного ───────────────────────── */

/**
 * Сколько занятий приезжает в счётчике.
 *
 * Полсотни, тем же числом, что у `CONVERSATION_LIST_LIMIT`, и по тому же
 * замеру: три коротких поля на строку это 1,3 КБ сжатых на пятьдесят строк.
 * Больше в видимой неделе и не бывает: два-три десятка занятий это обычная
 * полная неделя преподавателя.
 */
export const OCCURRENCE_CHAT_UNREAD_LIMIT = 50;

export const OccurrenceChatUnread = z.object({
  id: OccurrenceChatId,
  unread: z.number().int().nonnegative(),
  /** Время последнего сообщения: по нему открытая панель узнаёт, что пора перечитать. */
  at: z.number().int(),
});
export type OccurrenceChatUnread = z.infer<typeof OccurrenceChatUnread>;

/**
 * Ответ дешёвой ручки, которую опрашивает расписание: ни одного тела, ни одного
 * имени, три поля на занятие.
 *
 * ⚠️ **Счётчик отвечает по НЕСКОЛЬКИМ занятиям сразу**, иначе точка в
 * расписании невозможна: в неделе два-три десятка занятий, и тридцать запросов
 * ради точки никто не делает.
 *
 * ⚠️ **`id` это АДРЕС занятия ГЛАЗАМИ СМОТРЯЩЕГО, а не всегда его ключ.** Общий
 * чат группы лежит под ключом `group:<группа>:<дата>`, и преподаватель именно
 * так свою клетку и адресует (сводная строка сетки несёт `group.id`), а вот у
 * ученика сетка групповое занятие не сворачивает: его клетка это ЕГО слот.
 * Поэтому ученику та же строка приезжает адресом `pair:<его слот>:<дата>`.
 * Отдавать ему ключ значило бы отдавать адрес, который не совпадает ни с одной
 * его клеткой, то есть точку, которую негде поставить.
 */
export const OccurrenceChatUnreadResponse = z.object({
  /** Сумма по ВСЕМ занятиям окна, а не по приехавшим строкам. */
  total: z.number().int().nonnegative(),
  /**
   * Строки самых свежих занятий, не больше `OCCURRENCE_CHAT_UNREAD_LIMIT`.
   *
   * ⚠️ **Открытое занятие приезжает сюда ВСЕГДА, где бы оно ни стояло по
   * свежести**, и просит об этом клиент: `?id=<адрес>` поднимает его в начало
   * сортировки. Без этого счётчик слепнет на пятьдесят первом занятии молча, и
   * это не гипотеза: ровно так ослеп кабинетный счётчик, и это чинили правкой
   * (разбор у `ConversationUnreadResponse`). Молчаливая деградация запрещена
   * разделом 5.5 CLAUDE.md прямо.
   *
   * ⚠️ `id` тут ПОДСКАЗКА, а не адрес: незнакомый и мусорный ключ ничего не
   * поднимает и ничем не отказывает. Отказ был бы хуже поломки, которую он
   * ловит: занятие можно отменить из соседней вкладки, и 404 на счётчик увёл бы
   * весь опрос в отступ из-за одной исчезнувшей клетки.
   */
  items: z.array(OccurrenceChatUnread),
});
export type OccurrenceChatUnreadResponse = z.infer<typeof OccurrenceChatUnreadResponse>;
